/**
 * Filter Serialization
 *
 * Pure (no react-router) serialize/deserialize of a FilterSet to a URL-safe
 * base64 string. Kept separate from useFilterUrl so non-hook code (and tests)
 * can build/parse filter params without pulling in react-router.
 *
 * Wire format: base64(JSON([{ f: filterId, o: operator, v: value }]))
 */

import { FilterSet, FilterOperator, FilterValue } from './types';

function generateFilterId(): string {
  return `filter-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/** Serialize a filter set to a URL-safe base64 string ('' for empty). */
export function serializeFilters(filterSet: FilterSet): string {
  if (filterSet.length === 0) {
    return '';
  }

  const serialized = filterSet.map((filter) => ({
    f: filter.filterId,
    o: filter.operator,
    v: filter.value,
  }));

  try {
    return btoa(JSON.stringify(serialized));
  } catch {
    return '';
  }
}

/** Deserialize a filter set from a base64 string ([] on empty/invalid). */
export function deserializeFilters(serialized: string): FilterSet {
  if (!serialized) {
    return [];
  }

  try {
    const parsed = JSON.parse(atob(serialized)) as Array<{
      f: string;
      o: FilterOperator;
      v: FilterValue;
    }>;

    return parsed.map((item) => ({
      id: generateFilterId(),
      filterId: item.f,
      operator: item.o,
      value: item.v,
    }));
  } catch {
    return [];
  }
}
